// @system — Permission context for the admin area.
//
// Roles are rows in the server's `roles` table and every role has a permission
// set in `role_permissions` (editable on /app/admin/staff). The server answers
// GET /api/permissions/me with the caller's role + sorted permission keys;
// this provider fetches it ONCE per authenticated user (re-fetching only when
// `user.id` or `user.role` changes), skips the request entirely for regular
// members (`role === 'user'` never holds permissions) and clears everything on logout.
//
// Mount <PermissionsProvider> right inside <AuthProvider>. Components read it
// through usePermissions(); outside a provider the hook returns a deny-all
// value so shared components (Sidebar, ProtectedRoute) stay renderable in
// isolation and never grant access by accident.
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AuthContext } from '@/app/store/@system/auth'
import { getMyPermissions } from '@/app/api/@system/roles'
import { isStaffRole } from '@/app/lib/@system/roles'

/**
 * @typedef {Object} PermissionsValue
 * @property {ReadonlySet<string>} permissions - Permission keys the current user holds
 * @property {string|null} role - Current role slug (`owner | admin | staff | user | …`) or null when logged out
 * @property {boolean} loading - True while the first fetch for this user is in flight
 * @property {'db'|'defaults'|null} source - Where the server read the matrix from; null when nothing was fetched
 * @property {Error|null} error - Last fetch error (permissions are then empty)
 * @property {(permission: string) => boolean} can - True when the user holds `permission`
 * @property {(permissions: string[]) => boolean} canAny - True when the user holds at least one of `permissions`
 * @property {() => Promise<void>} refresh - Re-fetch the permission set (after editing the matrix)
 */

const EMPTY = Object.freeze(new Set())

/** @type {PermissionsValue} */
const DENY_ALL = Object.freeze({
  permissions: EMPTY,
  role: null,
  loading: false,
  source: null,
  error: null,
  can: () => false,
  canAny: () => false,
  refresh: async () => {},
})

export const PermissionsContext = createContext(/** @type {PermissionsValue|null} */ (null))

/**
 * Provides the current user's permission set. Must sit inside <AuthProvider>.
 * @param {{ children: import('react').ReactNode }} props
 */
export function PermissionsProvider({ children }) {
  const auth = useContext(AuthContext)
  const user = auth?.user ?? null
  const authLoading = auth?.loading ?? false
  const userId = user?.id ?? null
  const userRole = user?.role ?? null
  const needsFetch = userId != null && isStaffRole(userRole)

  const [state, setState] = useState(
    /** @type {{ permissions: ReadonlySet<string>, source: PermissionsValue['source'], error: Error | null, loading: boolean }} */
    ({ permissions: EMPTY, source: null, error: null, loading: needsFetch }),
  )
  const seq = useRef(0)

  const load = useCallback(async () => {
    const id = ++seq.current
    setState((prev) => ({ ...prev, loading: true, error: null }))
    try {
      const res = await getMyPermissions()
      if (id !== seq.current) return
      const list = Array.isArray(res?.data?.permissions) ? res.data.permissions : []
      setState({ permissions: new Set(list), source: res?.data?.source ?? null, error: null, loading: false })
    } catch (err) {
      if (id !== seq.current) return
      setState({ permissions: EMPTY, source: null, error: err instanceof Error ? err : new Error(String(err)), loading: false })
    }
  }, [])

  // One fetch per authenticated back-office user; customers and logged-out
  // visitors get an empty set without a request.
  useEffect(() => {
    if (needsFetch) {
      load()
      return
    }
    seq.current += 1 // invalidate any in-flight response from the previous user
    setState({ permissions: EMPTY, source: null, error: null, loading: false })
  }, [needsFetch, userId, userRole, load])

  const value = useMemo(() => {
    const { permissions } = state
    return {
      permissions,
      role: userRole,
      // While the session itself is still resolving we do not yet know whether
      // a fetch will be needed — report loading so guards show a spinner
      // instead of a denied state.
      loading: state.loading || (authLoading && userId == null),
      source: state.source,
      error: state.error,
      can: (/** @type {string} */ permission) => permissions.has(permission),
      canAny: (/** @type {string[]} */ list) => Array.isArray(list) && list.some((p) => permissions.has(p)),
      refresh: needsFetch ? load : DENY_ALL.refresh,
    }
  }, [state, userRole, authLoading, userId, needsFetch, load])

  // createElement rather than JSX: this file is `.js` like its sibling hooks.
  return createElement(PermissionsContext.Provider, { value }, children)
}

/**
 * Read the current user's permissions. Outside a provider this returns a
 * deny-all value (no throw) so shared components stay renderable in tests.
 * @returns {PermissionsValue}
 */
export function usePermissions() {
  return useContext(PermissionsContext) ?? DENY_ALL
}
